'use client'

/* ============================================================
   <BrandLoader> — animated brand loading screen.

   Shows the real logo mark with a soft light "shimmer" sweeping
   across it, paired with a fixed brand line and a rotating logistics
   status message so the wait reads as "work is happening", not a
   generic spinner.

   Single source of truth in @rr/ui. Apps use:
     <BrandLoader fullscreen />              // app / page boot
     <BrandLoader />                         // inline block (page-level)
   ============================================================ */

import * as React from 'react'
import { cn } from '../../utils'
import { Logo } from './logo'

/** Rotating status lines — feel like operations are underway. */
const DEFAULT_STATUSES = [
  'Calculating routes…',
  'Preparing shipments…',
  'Checking carriers…',
  'Loading quotes…',
  'Optimizing capacity…',
  'Syncing operations…',
  'Connecting your workspace…',
]

export interface BrandLoaderProps {
  /** Cover the viewport (app / page boot). Otherwise renders as a block. */
  fullscreen?: boolean
  /** Mark size in px. */
  size?: number
  /** Override the rotating status messages. */
  statuses?: string[]
  /**
   * The wordmark under the logo. Defaults to "route & route". Pass `null` to
   * hide it.
   */
  brandLine?: string | null
  /**
   * App-label badge rendered beneath the logo (e.g. "Admin", "Carrier").
   * Identifies which sub-app is loading.
   */
  label?: string
  /** ms between status rotations. */
  interval?: number
  className?: string
}

export function BrandLoader({
  fullscreen = false,
  size = 72,
  statuses = DEFAULT_STATUSES,
  brandLine = 'route & route',
  label,
  interval = 1800,
  className,
}: BrandLoaderProps) {
  const [statusIdx, setStatusIdx] = React.useState(0)

  React.useEffect(() => {
    if (statuses.length <= 1) return
    const id = setInterval(
      () => setStatusIdx((i) => (i + 1) % statuses.length),
      interval,
    )
    return () => clearInterval(id)
  }, [statuses.length, interval])

  const body = (
    <div className={cn('flex flex-col items-center justify-center text-center', className)}>
      <ShimmerMark size={size} />

      {label && (
        <span className="mt-4 rounded-full border border-border bg-muted px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
      )}

      {brandLine && (
        <p className="mt-3 text-[16px] font-semibold lowercase tracking-tight text-foreground">
          {brandLine}
        </p>
      )}

      <p
        key={statusIdx}
        className="rr-bl-fade mt-1.5 text-[12.5px] text-muted-foreground"
        aria-live="polite"
      >
        {statuses[statusIdx]}
      </p>
    </div>
  )

  if (!fullscreen) return body

  return (
    <div
      role="status"
      aria-label="Loading"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background"
    >
      {body}
    </div>
  )
}

// ── The animated mark ────────────────────────────────────────────────────────
// The REAL logo mark with a soft light "shimmer" sweeping diagonally across
// it, plus a gentle breathing scale. Uses the actual <Logo> (white mark on its
// cobalt badge) — no invented shapes. CSS is scoped + inlined so it animates in
// any app without depending on preset keyframes.
function ShimmerMark({ size }: { size: number }) {
  return (
    <div className="rr-bl-root" aria-hidden>
      <div
        className="rr-bl-mark relative inline-flex overflow-hidden"
        style={{ borderRadius: Math.round(size * 0.22) }}
      >
        <Logo size={size} />
        {/* Diagonal light sweep clipped to the badge */}
        <span className="rr-bl-shimmer" />
      </div>

      <style>{`
        @keyframes rr-bl-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes rr-bl-shimmer {
          0%   { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
          35%  { opacity: 1; }
          100% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
        }
        @keyframes rr-bl-status-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rr-bl-root .rr-bl-mark {
          animation: rr-bl-breathe 2.4s ease-in-out infinite;
        }
        .rr-bl-root .rr-bl-shimmer {
          position: absolute; top: 0; bottom: 0; left: 0;
          width: 45%;
          background: linear-gradient(
            100deg,
            transparent 0%,
            rgba(255,255,255,0.45) 50%,
            transparent 100%
          );
          animation: rr-bl-shimmer 1.8s ease-in-out infinite;
          pointer-events: none;
        }
        .rr-bl-fade { animation: rr-bl-status-fade 0.4s ease-out; }
        @media (prefers-reduced-motion: reduce) {
          .rr-bl-root .rr-bl-mark,
          .rr-bl-root .rr-bl-shimmer { animation: none; }
          .rr-bl-root .rr-bl-shimmer { display: none; }
          .rr-bl-fade { animation: none; }
        }
      `}</style>
    </div>
  )
}
